package com.arenaproplay.admin.ui.components.widgets

import androidx.compose.runtime.Composable
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.theme.withAlpha
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

val SpinKeyframes = Keyframes {
    from {
        Modifier.styleModifier { property("transform", "rotate(0deg)") }
    }
    to {
        Modifier.styleModifier { property("transform", "rotate(360deg)") }
    }
}

@Composable
fun LoadingSpinner(
    modifier: Modifier = Modifier,
    size: CSSLengthOrPercentageNumericValue = 40.px,
    text: String? = null
) {
    Box(
        modifier = modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.gap(16.px)
        ) {
            Box(
                modifier = Modifier
                    .width(size)
                    .height(size)
                    .border(
                        width = 3.px,
                        style = LineStyle.Solid,
                        color = AppColors.Primary.withAlpha(0.3f)
                    )
                    .styleModifier {
                        property("border-top-color", AppColors.Primary.toRgbString())
                    }
                    .borderRadius(50.percent)
                    .animation(
                        SpinKeyframes.toAnimation(
                            duration = 1.s,
                            timingFunction = AnimationTimingFunction.Linear
                        )
                    )
                    .styleModifier {
                        property("animation-iteration-count", "infinite")
                    }
            )

            text?.let {
                SpanText(
                    text = it,
                    modifier = Modifier
                        .color(AppColors.Primary)
                        .fontSize(14.px)
                )
            }
        }
    }
}

@Composable
fun InlineSpinner(
    modifier: Modifier = Modifier,
    size: CSSLengthOrPercentageNumericValue = 20.px
) {
    Box(
        modifier = modifier
            .width(size)
            .height(size)
            .border(
                width = 2.px,
                style = LineStyle.Solid,
                color = AppColors.Primary.withAlpha(0.3f)
            )
            .styleModifier {
                property("border-top-color", AppColors.Primary.toRgbString())
            }
            .borderRadius(50.percent)
            .animation(
                SpinKeyframes.toAnimation(
                    duration = 0.8.s,
                    timingFunction = AnimationTimingFunction.Linear
                )
            )
            .styleModifier {
                property("animation-iteration-count", "infinite")
            }
    )
}

/**
 * Compact ring spinner with explicit colors — used inside skeleton/placeholder
 * cards. Dark variant uses cream tones so it reads on the #0F1222 surface.
 */
@Composable
fun CardSpinner(
    dark: Boolean = false,
    size: CSSLengthOrPercentageNumericValue = 28.px
) {
    val ring = if (dark) "rgba(15,18,34,0.12)" else "rgba(15,18,34,0.12)"
    val accent = if (dark) "#FFFFFF" else "#0F1222"
    Box(
        Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Box(
            Modifier
                .width(size)
                .height(size)
                .borderRadius(50.percent)
                .styleModifier {
                    property("border", "2.5px solid $ring")
                    property("border-top-color", accent)
                    property("animation", "admin-spinner 900ms linear infinite")
                }
        )
    }
}
